import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Sparkles, AlertTriangle } from "lucide-react";

/** Compact banner that appears at the top of the app shell once a user is logged in.
 *
 *  Three states:
 *   1. On-trial (tier=pro, on_trial=true): "X days left in your free Pro trial → Upgrade"
 *   2. Trial expired & tier=free (trial_used=true): "Trial ended — upgrade to keep AI reports + sharing"
 *   3. Otherwise (paid tier or master admin): nothing.
 *
 *  Master admin (role=admin) bypasses tier gates so the banner gives them a friendly
 *  status pill instead of an upgrade nag.
 */
const dayDiff = (iso) => {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / 86400000);
  } catch {
    return null;
  }
};

const TrialBanner = () => {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "admin") return null; // master admin doesn't need to upgrade
  if (user.team_owner_id) return null;    // team members inherit the owner's plan — no upgrade nag

  const onTrial = !!user.on_trial && user.tier === "pro";
  if (!onTrial) return null; // expired/lapsed accounts are handled by AccessBanner

  if (onTrial) {
    const days = dayDiff(user.tier_expires_at);
    const urgent = days !== null && days <= 3;
    return (
      <div
        data-testid="trial-banner"
        className={`w-full border-b-2 ${urgent ? "bg-amber-50 border-amber-300" : "bg-teal-50 border-teal-200"}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2 font-bold text-slate-900">
            <Sparkles size={16} strokeWidth={2.5} className={urgent ? "text-amber-600" : "text-teal-600"} />
            <span data-testid="trial-banner-text">
              {days === 0
                ? "Your free Pro trial ends today"
                : days === 1
                ? "1 day left in your free Pro trial"
                : `${days} days left in your free Pro trial`}
            </span>
          </div>
          <Link
            to="/pricing"
            data-testid="trial-banner-upgrade"
            className="inline-flex items-center gap-1.5 h-8 px-3 bg-[#1E1B4B] text-white text-xs font-bold uppercase tracking-wide hover:bg-[#0F0E2E]"
          >
            Upgrade to keep Pro
          </Link>
        </div>
      </div>
    );
  }

  // trialEnded
  return (
    <div data-testid="trial-banner" className="w-full bg-red-50 border-b-2 border-red-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 font-bold text-slate-900">
          <AlertTriangle size={16} strokeWidth={2.5} className="text-red-600" />
          <span data-testid="trial-banner-text">
            Your free trial has ended — AI reports &amp; customer sharing are locked
          </span>
        </div>
        <Link
          to="/pricing"
          data-testid="trial-banner-upgrade"
          className="inline-flex items-center gap-1.5 h-8 px-3 bg-red-600 text-white text-xs font-bold uppercase tracking-wide hover:bg-red-700"
        >
          Upgrade to Pro
        </Link>
      </div>
    </div>
  );
};

export default TrialBanner;
